import { z } from 'zod';
import { PointSchema } from './geojson';

/**
 * Raw hotspot feature (Phase E.W.0.5) — a PRE-FUSION satellite thermal anomaly
 * served by GET /api/hotspots/raw. Backed directly by the E.1 `hotspot_detections`
 * table (FIRMS NRT + CWFIS M3 + GOES-R FDCC/FDCF), with NO fusion scoring.
 *
 * This is the lower-confidence SIBLING of the F.1-scored high_risk_hotspots
 * (high-risk-hotspot.ts): a raw detection tells you "a satellite saw heat here",
 * not "the fusion engine scored this as a fire". Raw hotspots are an AMBIENT map
 * layer (operator-cop-doctrine §3B.8) — never an actionable Anomaly-Queue dot —
 * and feed the F.1 fusion engine that produces the actionable high_risk_hotspots.
 *
 * Two shapes: the LIST endpoint returns the common feature; the DETAIL endpoint
 * (`/:id`) adds the ingest provenance + a co-located F.1 fused-hotspot link
 * (within 1 km), so the operator can jump from the raw signal to its scored peer.
 */

/** Ingest sources that land into hotspot_detections (open-ended — the render
 *  tolerates an unknown source via a grey fallback, never dropped). */
export const RAW_HOTSPOT_SOURCES = ['firms-nrt', 'cwfis-m3', 'goes-r-fdcc', 'goes-r-fdcf'] as const;

export const RawHotspotPropertiesSchema = z.object({
  id: z.string().uuid(),
  /** 'firms-nrt' | 'cwfis-m3' | 'goes-r-fdcc' | 'goes-r-fdcf' | … (other → grey ambient). */
  source: z.string(),
  satellite: z.string().nullable(),
  sensor: z.string().nullable(),
  /** Satellite overpass time (UTC ISO). */
  observed_at: z.string(),
  /** Fire radiative power (MW), when reported. */
  frp: z.number().nullable(),
  /** Numeric detection confidence (0–100), when reported. */
  confidence: z.number().nullable(),
  /** Raw confidence token as reported ('l'/'n'/'h' or numeric string). */
  confidence_raw: z.string().nullable(),
  /** Along-/cross-scan pixel footprint (km), from attrs when present. */
  scan: z.number().nullable(),
  track: z.number().nullable(),
});
export type RawHotspotProperties = z.infer<typeof RawHotspotPropertiesSchema>;

export const RawHotspotFeatureSchema = z.object({
  type: z.literal('Feature'),
  id: z.string().uuid(),
  geometry: PointSchema,
  properties: RawHotspotPropertiesSchema,
});
export type RawHotspotFeature = z.infer<typeof RawHotspotFeatureSchema>;

export const RawHotspotFeatureCollectionSchema = z.object({
  type: z.literal('FeatureCollection'),
  features: z.array(RawHotspotFeatureSchema),
});
export type RawHotspotFeatureCollection = z.infer<typeof RawHotspotFeatureCollectionSchema>;

/** The detail endpoint adds ingest provenance + the co-located fused-hotspot link. */
export const RawHotspotDetailSchema = z.object({
  type: z.literal('Feature'),
  id: z.string().uuid(),
  geometry: PointSchema,
  properties: RawHotspotPropertiesSchema.extend({
    agency: z.string().nullable(),
    /** CWFIS-only fire-weather index carried on the raw detection. */
    fwi: z.number().nullable(),
    est_area_ha: z.number().nullable(),
    /** When WE wrote the row (ingest provenance / attribution). */
    ingested_at: z.string(),
    /** The nearest LIVE F.1-scored high_risk_hotspot within 1 km, if any — lets
     *  the operator open the actionable scored sibling of this raw signal. */
    fused_hotspot_nearby: z
      .object({
        id: z.string().uuid(),
        confidence: z.number(),
        response_level: z.number(),
        distance_m: z.number(),
      })
      .nullable(),
  }),
});
export type RawHotspotDetail = z.infer<typeof RawHotspotDetailSchema>;
